import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUp from '@mui/icons-material/ThumbUp';
import ThumbDown from '@mui/icons-material/ThumbDown';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { deleteThread, upvoteThread, downvoteThread, reportThread } from '../services/api';
import EditThreadDialog from './EditThreadDialog';
import IconOnlyChip from './IconOnlyChip';
import IconTextChip from './IconTextChip';
import { isThreadEdited } from '../utils/isEdited';
import getUserDisplayName from '../utils/getUserDisplayName';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import ActionReasonDialog from './ActionReasonDialog';
import { POST_REPORT_REASONS } from '../utils/moderationReasons';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import EditedTag from './EditedTag';

const getPreview = (content) => {
  if (!content) return '';
  const doc = new DOMParser().parseFromString(content, 'text/html');
  const paragraphs = doc.querySelectorAll('p');
  return paragraphs.length > 0 ? paragraphs[0].textContent.trim() : '';
};

const timeAgo = (dateString) => {
  const now = new Date();
  const past = new Date(dateString);
  const diffInSeconds = Math.floor((now - past) / 1000);
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function ThreadCard({ thread, disableVoting = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, emailVerified } = useAuth();
  const verifiedUser = user && emailVerified;
  const [editOpen, setEditOpen] = useState(false);
  const [threadData, setThreadData] = useState(thread);
  const [upvotes, setUpvotes] = useState(thread.upvotes || 0);
  const [downvotes, setDownvotes] = useState(thread.downvotes || 0);
  const [upvoted, setUpvoted] = useState(thread.upvoted || false);
  const [downvoted, setDownvoted] = useState(thread.downvoted || false);
  const [reported, setReported] = useState(thread.reported || false);
  const [reportOpen, setReportOpen] = useState(false);
  useEffect(() => {
    setThreadData(thread);
    setUpvotes(thread.upvotes || 0);
    setDownvotes(thread.downvotes || 0);
    setUpvoted(thread.upvoted || false);
    setDownvoted(thread.downvoted || false);
    setReported(thread.reported || false);
  }, [thread]);
  const author = threadData.created_by || {};
  const isAuthor = user?.id === author.id;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const handleAuthorClick = (e) => {
    e.stopPropagation();
    const name = getUserDisplayName(author);
    if (!name) return;
    const path = `/community/user/${encodeURIComponent(name)}`;
    if (location.pathname !== path) {
      navigate(path, { state: { from: location.pathname } });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteThread(threadData.id);
      setDeleted(true);
    } catch (err) {
      console.error('Failed to delete thread', err);
    } finally {
      setConfirmOpen(false);
    }
  };

  const handleUpvote = async () => {
    try {
      const res = await upvoteThread(threadData.id);
      const { upvotes, downvotes, upvoted, downvoted } = res.data;
      setUpvotes(upvotes);
      setDownvotes(downvotes);
      setUpvoted(upvoted);
      setDownvoted(downvoted);
    } catch (err) {
      console.error('Upvote error:', err);
    }
  };

  const handleDownvote = async () => {
    try {
      const res = await downvoteThread(threadData.id);
      const { upvotes, downvotes, upvoted, downvoted } = res.data;
      setUpvotes(upvotes);
      setDownvotes(downvotes);
      setUpvoted(upvoted);
      setDownvoted(downvoted);
    } catch (err) {
      console.error('Downvote error:', err);
    }
  };

  const handleReport = async (reason) => {
    try {
      await reportThread(threadData.id, reason);
      setReported(true);
    } catch (err) {
      console.error('Failed to report thread', err);
    } finally {
      setReportOpen(false);
    }
  };

  if (deleted) return null;

  return (
    <>
    <Card
      sx={{
        mb: 2,
        border: '1px solid #ddd',
        boxShadow: 'none',
        '&:hover': { boxShadow: 2 },
        cursor: 'pointer',
      }}
      onClick={() =>
        navigate(`/community/threads/${threadData.id}`, {
          state: { from: location.pathname },
        })
      }
    >
      <CardContent
        sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 0.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 'bold', flex: 1, wordBreak: 'break-word' }}
          >
            {threadData.title}
          </Typography>
          <IconTextChip
            icon={reported ? <FlagIcon /> : <OutlinedFlagIcon />}
            label={reported ? 'Reported' : 'Report'}
            color="error"
            onClick={verifiedUser ? () => setReportOpen(true) : undefined}
            disabled={reported || !verifiedUser}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <IconTextChip
            icon={
              <Avatar
                src={author.avatar || undefined}
                alt={author.display_name || author.username || 'Anonymous'}
                sx={{ width: 24, height: 24 }}
              >
                {!author.avatar && (author.display_name || author.username || 'A')[0]}
              </Avatar>
            }
            label={author.display_name || author.username || 'Anonymous'}
            onClick={handleAuthorClick} sx={{pl:0}}
          />
          {threadData.topicName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip
                label={threadData.topicName}
                size="small"
                component={Link}
                to={`/community/topic/${encodeURIComponent(threadData.topicName)}`}
                state={{ from: location.pathname }}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  fontWeight: 'bold',
                  border: `2px solid ${threadData.topicColor}`,
                  backgroundColor: `${threadData.topicColor}20`,
                }}
              />
              {threadData.subtopicName && (
                <>
                  <ChevronRightRoundedIcon fontSize="small" color="action" />
                  {threadData.subtopicId ? (
                    <Chip
                      label={threadData.subtopicName}
                      size="small"
                      component={Link}
                      to={`/community/topic/${encodeURIComponent(
                        threadData.topicName
                      )}?subs=${threadData.subtopicId}`}
                      state={{ from: location.pathname }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <Chip label={threadData.subtopicName} size="small" />
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ my: 1, mx: 0.5 }}
        >
          {getPreview(threadData.content)}
          {isThreadEdited(threadData) &&
            !editOpen &&
            !['[deleted]', '[removed]'].includes((threadData.content || '').trim()) && (
              <>
                {' '}
                <EditedTag />
              </>
            )}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          <IconTextChip
            icon={<CalendarTodayOutlinedIcon fontSize="small" />}
            label={
              new Date() - new Date(threadData.created_at) > 86400000
                ? formatDate(threadData.created_at)
                : timeAgo(threadData.created_at)
            }
            color="default"
          />
          {threadData.view_count !== undefined && (
            <IconTextChip
              icon={<VisibilityOutlinedIcon />}
              label={threadData.view_count}
              color="default"
            />
          )}
          <IconTextChip
            icon={<ChatBubbleOutlineOutlinedIcon />}
            label={threadData.repliesCount || 0}
            color="default"
            onClick={() =>
              navigate(`/community/threads/${threadData.id}`, {
                state: { from: location.pathname },
              })
            }
          />
          <IconTextChip
            icon={upvoted ? <ThumbUp /> : <ThumbUpOffAltIcon />}
            label={upvotes}
            color="success"
            onClick={verifiedUser ? handleUpvote : undefined}
            disabled={disableVoting || !verifiedUser}
          />
          <IconTextChip
            icon={downvoted ? <ThumbDown /> : <ThumbDownOffAltIcon />}
            label={downvotes}
            color="success"
            onClick={verifiedUser ? handleDownvote : undefined}
            disabled={disableVoting || !verifiedUser}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            {verifiedUser && isAuthor && (
              <>
                <IconOnlyChip
                  icon={<EditOutlinedIcon />}
                  color="success"
                  onClick={() => setEditOpen(true)}
                />
                <IconOnlyChip
                  icon={<DeleteOutlineOutlinedIcon />}
                  color="error"
                  onClick={() => setConfirmOpen(true)}
                />
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
      <DialogTitle>Delete Thread?</DialogTitle>
      <DialogContent>
        <Typography>Are you sure you want to delete this thread?</Typography>
      </DialogContent>
      <DialogActions>
        <OutlinedButton onClick={() => setConfirmOpen(false)} text="Cancel" color="success" />
        <FilledButton onClick={handleDelete} text="Delete" color="error" />
      </DialogActions>
    </Dialog>
    <EditThreadDialog
      open={editOpen}
      threadId={threadData.id}
      onClose={() => setEditOpen(false)}
      onUpdated={res => setThreadData(res.data || res)}
    />
    <ActionReasonDialog
      open={reportOpen}
      onClose={() => setReportOpen(false)}
      title="Report Thread"
      reasons={POST_REPORT_REASONS}
      onConfirm={handleReport}
    />
    </>
  );
}
